import React, { forwardRef, memo } from 'react';
import MuiTypography, { TypographyProps as MuiTypographyProps } from '@material-ui/core/Typography';
import styled from 'styled-components';
import * as tokens from '@utmost.co/utmost-design-tokens';
import { Uds } from '../../types';

type PartialTypographyProps = MuiTypographyProps<'span', {}>;

export type TypographyProps = Uds<
  Omit<PartialTypographyProps, 'variant' | 'color'> & {
    variant?: TypographyVariants;
    color?: string;
    tag?: string;
  }
>;

export enum FontWeight {
  LIGHT = 300,
  REGULAR = 400,
  MEDIUM = 500,
  BOLD = 700
}

export const Typography = forwardRef<any, TypographyProps>(
  ({ style, dataTest, color, tag, children, variant, className, ...rest }, ref) => (
    <StyledTypography
      {...rest}
      ref={ref}
      component={tag ?? 'div'}
      $color={color}
      className={`${className ?? ''} ${variant ?? defaultVariant}`}
      style={style}
      data-test={dataTest}
    >
      {children}
    </StyledTypography>
  )
);

(Typography as React.FC<React.PropsWithChildren<unknown>>).displayName = 'Typography';

const source = tokens.source;
const StyledTypography = styled(MuiTypography)<{ $color: string | undefined; component: string }>`
  color: ${(props) => props.$color || tokens.colorOnSurface};
  &.hint {
    ${getVariant('bodySmall')}
  }
  &.body1 {
    ${getVariant('bodyMedium')}
  }
  &.body1Bold {
    ${getVariant('bodyLarge')}
    font-weight: ${({ theme }) => theme.fontWeights.medium};
  }
  &.body2 {
    ${getVariant('bodySmall')}
  }
  &.h1 {
    ${getVariant('headingMedium')}
  }
  &.h2 {
    ${getVariant('headingSmall')}
  }
  &.h3 {
    ${getVariant('titleLarge')}
  }
  &.h4 {
    ${getVariant('titleMedium')}
  }
  &.h5 {
    ${getVariant('titleSmall')}
  }
  &.small {
    ${getVariant('bodySmall')}
  }
  &.label1 {
    ${getVariant('labelLarge')}
  }
  &.label2 {
    ${getVariant('labelMedium')}
  }
  &.labelSmall {
      ${getVariant('labelSmall')}
  }
`;

type NewTypographyVariants = 'display' | 'heading' | 'title' | 'body' | 'label';
type Size = 'Small' | 'Medium' | 'Large';

function getVariant(variant: `${NewTypographyVariants}${Size}`) {
  return `font-weight: ${source[`${variant}FontWeight`]};
    font-size: ${source[`${variant}FontSize`]}px;
    line-height: ${source[`${variant}LineHeight`]}px;
    letter-spacing: ${source[`${variant}LetterSpacing`]}px;`;
}

StyledTypography.displayName = 'StyledTypography';

const defaultVariant: TypographyVariants = 'body1';

export type TypographyVariants =
  | 'hint'
  | 'body1'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'small'
  | 'label2'
  | 'label1'
  | 'body2'
  | 'body1Bold'
  | 'labelSmall';

const TypographyComponent = memo(Typography);
export default TypographyComponent;
TypographyComponent.displayName = 'Typography';
